<script lang="ts">
// Keep in sync with `ContactFormController`
export const DIAGNOSTIC_CATEGORIES = [
  'javascript',
  'cookies',
  'api.padlet.com',
  'whiteboard-server.padlet.com',
  'padletcdn.com',
  'padlet.pics',
  'padlet.net',
  'cdnjs.cloudflare.com',
  'googleapis.com',
  'googleusercontent.com',
  'images.unsplash.com',
  'websocket',
  'u1.padletusercontent.com',
  'u2.padletusercontent.com',
  'xp.padletusercontent.com',
  'iframe.mediadelivery.net',
  'vz-72e428e7-67e.b-cdn.net',
  'sc-cdn.net',
  'camera-kit-api.snapar.com',
] as const
export type DiagnosticCategory = typeof DIAGNOSTIC_CATEGORIES[number]
export type DiagnosticResults = Record<DiagnosticCategory, boolean | undefined>
export default {}
</script>

<script setup lang="ts">
const diagnosticResults: DiagnosticResults = DIAGNOSTIC_CATEGORIES.reduce(
  (prev, category) =>
    Object.assign(
      prev,
      [
        'padlet.pics',
        'cdnjs.cloudflare.com',
        'googleapis.com',
        'googleusercontent.com',
        'images.unsplash.com',
        'sc-cdn.net',
        'camera-kit-api.snapar.com',
      ].includes(category)
        ? { [category]: false } // for async checks, we assume they are false by default
        : { [category]: undefined }, // for other checks, they are undefined by default
    ),
  {},
) as DiagnosticResults

const runDiagnostics = async (): Promise<void> => {
  await Promise.all(
    DIAGNOSTIC_CATEGORIES.map(async (category) => {
      const result = await runDiagnostic(category)
      diagnosticResults[category] = result
    }),
  )
}

function asyncLoadImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const image = new Image()
    let done = false
    image.onload = function () {
      resolve(true)
      done = false
    }
    image.src = url
    setTimeout(() => {
      if (!done) resolve(false)
    }, 3000)
  })
}

// Note: asyncLoadAudio will always fail for some browsers like Mobile Safari
// because Mobile Safari doesn't load audio until it is played so the tests using Audio's onloadeddata doesn't work
// => We should use images for things to work across browsers
// function asyncLoadAudio(url: string): Promise<boolean> {
//   return new Promise((resolve) => {
//     const audio = new Audio(url)
//     let done = false
//     audio.onloadeddata = function () {
//       resolve(true)
//       done = false
//     }
//     audio.src = url
//     setTimeout(() => {
//       if (!done) resolve(false)
//     }, 3000)
//   })
// }

const runDiagnostic = async (category: DiagnosticCategory): Promise<boolean> => {
  switch (category) {
    case 'javascript':
      return true
    case 'cookies':
      return navigator.cookieEnabled
    case 'api.padlet.com':
      try {
        await fetch('https://api.padlet.com/api/5/users/12')
        return true
      } catch (e) {
        return false
      }
    case 'whiteboard-server.padlet.com':
      try {
        await fetch('https://whiteboard-server.padlet.com/hc')
        return true
      } catch (e) {
        return false
      }
    case 'padletcdn.com':
      return (window as any).domains?.['padletcdn.com']
    case 'padlet.pics':
      return asyncLoadImage('https://padlet.pics/1/image?url=https%3A%2F%2Fpadlet.com%2Ffavicon.ico')
    case 'padlet.net':
      return (window as any).domains?.['padlet.net']
    case 'cdnjs.cloudflare.com':
      return new Promise((resolve) => {
        const anchor = document.getElementsByTagName('script')[0] as HTMLElement
        const a = document.createElement('script')
        let done = false
        a.type = 'text/javascript'
        a.async = true
        a.src = 'https://cdnjs.cloudflare.com/ajax/libs/js-cookie/2.1.2/js.cookie.min.js'
        a.onload = function () {
          resolve(true)
          done = true
        }
        ;(anchor.parentNode as HTMLElement).insertBefore(a, anchor)
        setTimeout(() => {
          if (!done) resolve(false)
        }, 3000)
      })
    case 'googleapis.com':
      return asyncLoadImage(
        'https://padlet-uploads.storage.googleapis.com/93511621/281def7aba6021983b2db8f8972ccae0/Bishan_home.jpg',
      )

    case 'googleusercontent.com':
      return asyncLoadImage(
        'https://lh5.googleusercontent.com/LNhmlmZ83hCLPxEAik9nAHAS5XCrsVpx3EY4Nzp5EGRqFpwiDpp2wcq_Q5ILsbmLfejbd-_ll3GnTi1ICYJ-8CYPgH6iuOJgRFs=w32-h32-p',
      )
    case 'images.unsplash.com':
      return asyncLoadImage(
        'https://images.unsplash.com/photo-1579149254497-5b90981f0c93?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=100',
      )
    case 'websocket':
      return new Promise((resolve) => {
        const socket = new WebSocket('wss://padlet.com/diagnostics/websocket')
        let done = false
        socket.addEventListener('message', () => {
          resolve(true)
          done = true
          socket.close()
        })
        socket.addEventListener('open', () => {
          socket.send('ping')
        })
        setTimeout(() => {
          if (!done) resolve(false)
        }, 3000)
      })
    case 'u1.padletusercontent.com':
      return asyncLoadImage(
        'https://u1.padletusercontent.com/uploads/padlet-uploads/613524129/64bcedafe70575e5a6acd67fce5c963a/kawaii_cat_chi_saying_chi_o21zmwv5sojf8yqu.jpeg?token=yx56m-2CVSUIUuRIxSKBL9DuuU3emKBxCzrAwuekGa7CxN8E6C6xeg73sErewwiVohPrzj267fuFPHSEA6pTOhL-kCX1uXhmhA_RqcPII5vnscG209GD1buGGAqljT0BIYd4nRf_53OyRSY-B7I-0gaXA7UXRLx45ZWer7JMxaby6e9MCGGp2I-CtJL44ZRAH9xODQTaH__joE9EHvqxFrfR9y2F6HKtnJrCoKrJbpRKvTq7y2brZsALeOrEF-Bcjhv5ZLuEEOBmMt-M6PKNz0iZwmTIHQZC_gDfz_gZ2es=',
      )
    case 'u2.padletusercontent.com':
      try {
        const response = await fetch(
          'https://u2.padletusercontent.com/uploads/padlet-uploads/613524129/3cf784517b74e76eee11b8964fd30ca6/sample_3s.mp3\?token\=yx56m-2CVSUIUuRIxSKBL9DuuU3emKBxCzrAwuekGa7CxN8E6C6xeg73sErewwiVohPrzj267fuFPHSEA6pTOhL-kCX1uXhmhA_RqcPII5tuxBv8XHQEC5Jxwyp-uPppGtsKdz7JdLt6kR0jZNjeMdhFZnUDX1W1BnjqIx9-xmb_rqK9GiXz70VjBYJbsAweS7zNQZ5SMdA2YjPsDlYbulqvxCfLkPFkzN66IkoNjPFcN_eWd-4Tnwm_TM0JUaEQ',
          { method: 'HEAD' },
        )
        return response.headers.get('content-type') === 'audio/mpeg'
      } catch (e) {
        return false
      }
    case 'xp.padletusercontent.com':
      return asyncLoadImage(
        'https://xp.padletusercontent.com/proxy?url=https%3A%2F%2Fu1.padletusercontent.com%2Fuploads%2Fpadlet-uploads%2F613524129%2F64bcedafe70575e5a6acd67fce5c963a%2Fkawaii_cat_chi_saying_chi_o21zmwv5sojf8yqu.jpeg%3Ftoken%3Dyx56m-2CVSUIUuRIxSKBL9DuuU3emKBxCzrAwuekGa7CxN8E6C6xeg73sErewwiVohPrzj267fuFPHSEA6pTOhL-kCX1uXhmhA_RqcPII5vnscG209GD1buGGAqljT0BIYd4nRf_53OyRSY-B7I-0gaXA7UXRLx45ZWer7JMxaby6e9MCGGp2I-CtJL44ZRAH9xODQTaH__joE9EHvqxFrfR9y2F6HKtnJrCoKrJbpRKvTq7y2brZsALeOrEF-Bcjhv5ZLuEEOBmMt-M6PKNz0iZwmTIHQZC_gDfz_gZ2es%3D',
      )
    case 'iframe.mediadelivery.net':
      try {
        await fetch('https://iframe.mediadelivery.net/play/775/5faa213c-35e9-4bee-b01b-abab6c13f057')
        return true
      } catch (e) {
        return false
      }
    case 'vz-72e428e7-67e.b-cdn.net':
      try {
        await fetch('https://vz-72e428e7-67e.b-cdn.net/5faa213c-35e9-4bee-b01b-abab6c13f057/playlist.m3u8')
        return true
      } catch (e) {
        return false
      }
    case 'sc-cdn.net':
      try {
        await fetch('https://cf-st.sc-cdn.net/d/cLSplk9up0w6P1GYxKklJ/285/435/rel-simd-neh/LensCoreWebAssembly.js')
        return true
      } catch (e) {
        return false
      }
    case 'camera-kit-api.snapar.com':
      // Expect 401 here. fetch only rejects on network failures (no internet, firewall blocks, CORS failures)
      // HTTP error responses (404, 500, etc) will still resolve
      try {
        await fetch('https://camera-kit-api.snapar.com/com.snap.camerakit.v3.Metrics/GetInitializationConfig', {
          method: 'OPTIONS',
        })
        return true
      } catch (e) {
        return false
      }
  }
}
const emit = defineEmits(['result'])
runDiagnostics().then(() => {
  emit('result', diagnosticResults)
})
</script>

<template><div /></template>
